import { useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API, AuthContext } from "@/App";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { 
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import * as XLSX from "xlsx";
import { LogOut, ArrowLeft, CheckCircle, MoreVertical, UserCheck, Mail, Edit, Search, Filter, Users, TrendingUp, Clock, XCircle, FileText, Eye, Calendar, Key, ArrowRightLeft, Download, Trash2, Undo2 } from "lucide-react";

const CONSOLIDATED_GRADES = ["Preschool", "JK", "SK", "G1", "G2", "G3", "G4", "G5", "G6", "G7", "G8"];

const WaitlistManagement = () => {
  const navigate = useNavigate();
  const { logout } = useContext(AuthContext);
  const [stats, setStats] = useState([]);
  const [selectedGrade, setSelectedGrade] = useState(null);
  const [waitlist, setWaitlist] = useState([]);
  const [loading, setLoading] = useState(false);
  
  // Search and filter state
  const [searchTerm, setSearchTerm] = useState("");
  const [gradeFilter, setGradeFilter] = useState("all");
  const [filteredWaitlist, setFilteredWaitlist] = useState([]);
  const [birthYearSort, setBirthYearSort] = useState("none"); // none, newest, oldest
  
  // Edit dialog state
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editingApplication, setEditingApplication] = useState(null);
  const [editFormData, setEditFormData] = useState({
    parent_name: '',
    parent_email: '',
    parent_telephone: '',
    students: [],
    notes: ''
  });

  // Void application state
  const [showVoidPasswordDialog, setShowVoidPasswordDialog] = useState(false);
  const [showVoidReasonDialog, setShowVoidReasonDialog] = useState(false);
  const [voidingApplication, setVoidingApplication] = useState(null);
  const [voidPassword, setVoidPassword] = useState('');
  const [voidReason, setVoidReason] = useState('');
  const [customVoidReason, setCustomVoidReason] = useState('');
  const [showVoidedList, setShowVoidedList] = useState(false);
  const [voidedApplications, setVoidedApplications] = useState([]);
  const [voidedSearchTerm, setVoidedSearchTerm] = useState("");
  const [isVoiding, setIsVoiding] = useState(false);
  
  // Duplicate email merge state
  const [showDuplicateDialog, setShowDuplicateDialog] = useState(false);
  const [duplicateData, setDuplicateData] = useState(null);
  const [duplicateMergePassword, setDuplicateMergePassword] = useState('');
  const [isMergingDuplicates, setIsMergingDuplicates] = useState(false);
  const [mergeResult, setMergeResult] = useState(null);
  
  // Reject application state
  const [showRejectPasswordDialog, setShowRejectPasswordDialog] = useState(false);
  const [showRejectReasonDialog, setShowRejectReasonDialog] = useState(false);
  const [rejectingApplication, setRejectingApplication] = useState(null);
  const [rejectPassword, setRejectPassword] = useState('');
  const [rejectReason, setRejectReason] = useState('');
  const [customRejectReason, setCustomRejectReason] = useState('');
  
  // Interview scheduling state
  const [showInterviewDateDialog, setShowInterviewDateDialog] = useState(false);
  const [interviewApplication, setInterviewApplication] = useState(null);
  const [interviewDate, setInterviewDate] = useState('');
  const [interviewTime, setInterviewTime] = useState('');
  
  // Bulk import state
  const [showBulkImportDialog, setShowBulkImportDialog] = useState(false);
  const [bulkImportFile, setBulkImportFile] = useState(null);
  const [bulkImportPreview, setBulkImportPreview] = useState(null);
  const [isImporting, setIsImporting] = useState(false);
  
  // Processing applications state
  const [showProcessingList, setShowProcessingList] = useState(false);
  const [processingApplications, setProcessingApplications] = useState([]);
  
  // Global search state
  const [showGlobalSearch, setShowGlobalSearch] = useState(false);
  const [globalSearchTerm, setGlobalSearchTerm] = useState("");
  const [globalSearchResults, setGlobalSearchResults] = useState([]);
  const [isGlobalSearching, setIsGlobalSearching] = useState(false);
  
  // Password reset state
  const [showPasswordResetDialog, setShowPasswordResetDialog] = useState(false);
  const [showPasswordResetAuthDialog, setShowPasswordResetAuthDialog] = useState(false);
  const [resetPasswordItem, setResetPasswordItem] = useState(null);
  const [newPassword, setNewPassword] = useState("");
  const [authPassword, setAuthPassword] = useState("");
  const [resettingPassword, setResettingPassword] = useState(false);

  // Move student state
  const [showMoveStudentDialog, setShowMoveStudentDialog] = useState(false);
  const [moveStudentItem, setMoveStudentItem] = useState(null);
  const [moveTargetGrade, setMoveTargetGrade] = useState("");
  const [isMovingStudent, setIsMovingStudent] = useState(false);

  // Rearrange by DOB state
  const [showRearrangeDialog, setShowRearrangeDialog] = useState(false);
  const [isRearranging, setIsRearranging] = useState(false);
  const [rearrangeResults, setRearrangeResults] = useState(null);

  useEffect(() => {
    fetchStats();
  }, []);

  useEffect(() => {
    if (selectedGrade) {
      filterWaitlist();
    }
  }, [waitlist, searchTerm, birthYearSort]);

  const filterWaitlist = () => {
    let filtered = [...waitlist];

    // Filter by search term
    if (searchTerm.trim()) {
      filtered = filtered.filter(item => 
        `${item.parent_name} ${item.students.map(s => s.first_name + ' ' + s.last_name).join(' ')}`
          .toLowerCase()
          .includes(searchTerm.toLowerCase()) ||
        item.application_code.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Sort by birth year (for Preschool, JK, and SK)
    if (["Preschool", "JK", "SK"].includes(selectedGrade) && birthYearSort !== "none") {
      filtered.sort((a, b) => {
        // Get the first student's birth year from each application
        const yearA = a.students[0]?.date_of_birth ? new Date(a.students[0].date_of_birth).getFullYear() : 0;
        const yearB = b.students[0]?.date_of_birth ? new Date(b.students[0].date_of_birth).getFullYear() : 0;
        
        if (birthYearSort === "newest") {
          return yearB - yearA; // Newest first (higher year first)
        } else {
          return yearA - yearB; // Oldest first (lower year first)
        }
      });
    }

    setFilteredWaitlist(filtered);
  };

  const getTotalWaitlist = () => {
    return stats.reduce((sum, s) => sum + s.total_waiting, 0);
  };

  const getGradesWithWaitlist = () => {
    return stats.filter(s => s.total_waiting > 0).length;
  };

  const getTotalAvailableSpots = () => {
    return stats.reduce((sum, s) => sum + s.available_spots, 0);
  };

  const scrollToGrade = (grade) => {
    const element = document.getElementById(`grade-card-${grade}`);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/admin/waitlist-consolidated`);
      setStats(response.data);
    } catch (error) {
      toast.error("Failed to fetch statistics");
    }
  };

  const fetchGradeWaitlist = async (grade) => {
    setLoading(true);
    setShowGlobalSearch(false); // Close global search when selecting specific grade
    try {
      // Use consolidated endpoint for consolidated grades
      const endpoint = CONSOLIDATED_GRADES.includes(grade) 
        ? `/admin/waitlist-by-consolidated/${grade}`
        : `/admin/waitlist/${grade}`;
      
      const response = await axios.get(`${API}${endpoint}`);
      setWaitlist(response.data);
      setFilteredWaitlist(response.data);
      setSelectedGrade(grade);
      setSearchTerm(""); // Clear search when changing grades
      setBirthYearSort("none"); // Reset birth year sort when changing grades
    } catch (error) {
      toast.error("Failed to fetch waitlist");
    } finally {
      setLoading(false);
    }
  };
  
  const handleGlobalSearch = async () => {
    if (!globalSearchTerm.trim()) {
      toast.error("Please enter a search term");
      return;
    }
    
    setIsGlobalSearching(true);
    setShowGlobalSearch(true);
    setSelectedGrade(null); // Close any open grade view
    
    try {
      const response = await axios.get(`${API}/admin/search-all-waitlists?search=${encodeURIComponent(globalSearchTerm)}`);
      setGlobalSearchResults(response.data);
      
      if (response.data.length === 0) {
        toast.info(`No results found for "${globalSearchTerm}"`);
      } else {
        toast.success(`Found ${response.data.length} result(s)`);
      }
    } catch (error) {
      toast.error("Search failed. Please try again.");
    } finally {
      setIsGlobalSearching(false);
    }
  };

  const handleRequestInterview = (applicationId, grade) => {
    setInterviewApplication({ id: applicationId, grade: grade });
    setShowInterviewDateDialog(true);
  };

  const handleInterviewDateSubmit = async () => {
    if (!interviewDate || !interviewTime) {
      toast.error("Please select both date and time for the interview");
      return;
    }

    // Validate time is before 3:00 PM
    const [hours, minutes] = interviewTime.split(':');
    const timeInMinutes = parseInt(hours) * 60 + parseInt(minutes);
    const maxTimeInMinutes = 15 * 60; // 3:00 PM
    
    if (timeInMinutes >= maxTimeInMinutes) {
      toast.error("Interviews cannot be scheduled after 3:00 PM");
      return;
    }

    try {
      await axios.post(`${API}/admin/request-interview`, {
        application_id: interviewApplication.id,
        grade: interviewApplication.grade,
        interview_date: interviewDate,
        interview_time: interviewTime
      });
      
      toast.success("Interview scheduled successfully");
      setShowInterviewDateDialog(false);
      setInterviewApplication(null);
      setInterviewDate('');
      setInterviewTime('');
      
      fetchGradeWaitlist(selectedGrade);
      fetchProcessingApplications();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to schedule interview");
    }
  };

  const handleAcceptStudent = async (applicationId, grade) => {
    try {
      await axios.post(`${API}/admin/accept-student`, {
        application_id: applicationId,
        grade: grade
      });
      toast.success("Student accepted and waitlist updated");
      fetchGradeWaitlist(selectedGrade);
      fetchStats();
    } catch (error) {
      toast.error("Failed to accept student");
    }
  };

  const handleEditRecord = async (item) => {
    try {
      // Fetch full application details with retry logic
      let application = null;
      let attempts = 0;
      const maxAttempts = 3;
      
      while (!application && attempts < maxAttempts) {
        try {
          attempts++;
          const response = await axios.get(`${API}/applications/${item.application_id}`, {
            timeout: 10000, // 10 second timeout
            headers: {
              'Cache-Control': 'no-cache',
              'Pragma': 'no-cache'
            }
          });
          application = response.data;
        } catch (err) {
          if (attempts >= maxAttempts) {
            throw err;
          }
          // Wait before retry (exponential backoff)
          await new Promise(resolve => setTimeout(resolve, 1000 * attempts));
        }
      }
      
      // Validate application data
      if (!application || !application.students || !Array.isArray(application.students)) {
        throw new Error("Invalid application data structure");
      }
      
      setEditingApplication(item);
      setEditFormData({
        parent_name: item.parent_name,
        parent_email: item.parent_email,
        parent_telephone: item.parent_telephone,
        students: application.students.map(s => ({
          first_name: s.first_name || '',
          last_name: s.last_name || '',
          gender: s.gender || '',
          date_of_birth: s.date_of_birth || '',
          grade_applying: s.grade_applying || '',
          current_school: s.current_school || ''
        })),
        notes: application.notes || ''
      });
      setShowEditDialog(true);
    } catch (error) {
      console.error("Error loading application details:", error);
      const errorMessage = error.response?.data?.detail || error.message || "Failed to load application details";
      toast.error(errorMessage + " - Please try again or contact support.");
    }
  };

  const handleSaveEdit = async () => {
    try {
      await axios.put(`${API}/admin/update-application/${editingApplication.application_id}`, editFormData);
      toast.success("Application updated successfully");
      setShowEditDialog(false);
      fetchGradeWaitlist(selectedGrade);
    } catch (error) {
      toast.error("Failed to update application");
    }
  };


  const handleVoidApplication = (item) => {
    setVoidingApplication(item);
    setVoidPassword('');
    setVoidReason('');
    setCustomVoidReason('');
    setShowVoidPasswordDialog(true);
  };

  const handleVoidPasswordSubmit = () => {
    if (!voidPassword.trim()) {
      toast.error("Please enter the void permission password");
      return;
    }
    setShowVoidPasswordDialog(false);
    setShowVoidReasonDialog(true);
  };

  const handleVoidReasonSubmit = async () => {
    if (!voidReason) {
      toast.error("Please select a void reason");
      return;
    }

    if (voidReason === 'other' && !customVoidReason.trim()) {
      toast.error("Please specify the reason");
      return;
    }

    const finalReason = voidReason === 'other' ? customVoidReason : voidReason;

    setIsVoiding(true);
    toast.info("Voiding application...", { duration: 2000 });

    try {
      await axios.post(`${API}/admin/void-application`, {
        application_id: voidingApplication.application_id,
        password: voidPassword,
        void_reason: finalReason
      });
      
      toast.success("Application voided successfully");
      setShowVoidReasonDialog(false);
      setVoidingApplication(null);
      setVoidPassword('');
      setVoidReason('');
      setCustomVoidReason('');
      
      fetchGradeWaitlist(selectedGrade);
      fetchStats();
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error("Invalid void permission password");
        setShowVoidReasonDialog(false);
        setShowVoidPasswordDialog(true);
      } else {
        toast.error(error.response?.data?.detail || "Failed to void application");
        setShowVoidReasonDialog(false);
      }
    } finally {
      setIsVoiding(false);
    }
  };

  const fetchVoidedApplications = async () => {
    try {
      const response = await axios.get(`${API}/admin/voided-applications`);
      setVoidedApplications(response.data);
    } catch (error) {
      toast.error("Failed to fetch voided applications");
    }
  };

  const handleShowVoidedList = () => {
    setShowVoidedList(true);
    fetchVoidedApplications();
  };

  const handleRestoreVoidedApplication = async (applicationId, applicationCode) => {
    if (!window.confirm(`Restore application ${applicationCode} back to the waitlist in its original position?`)) return;
    try {
      await axios.post(`${API}/admin/restore-voided-application`, { application_id: applicationId });
      toast.success(`${applicationCode} restored to waitlist`);
      fetchVoidedApplications();
      fetchStats();
      if (selectedGrade) fetchGradeWaitlist(selectedGrade);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to restore application");
    }
  };

  const handleShowDuplicates = async () => {
    setShowDuplicateDialog(true);
    setMergeResult(null);
    setDuplicateMergePassword('');
    try {
      const response = await axios.get(`${API}/admin/duplicate-emails`);
      setDuplicateData(response.data);
    } catch (error) {
      toast.error("Failed to fetch duplicate emails");
    }
  };

  const handleMergeDuplicates = async () => {
    if (!duplicateMergePassword) {
      toast.error("Authorization password is required");
      return;
    }
    setIsMergingDuplicates(true);
    try {
      const response = await axios.post(`${API}/admin/merge-duplicate-emails`, {
        password: duplicateMergePassword,
        dry_run: false
      });
      setMergeResult(response.data);
      toast.success(`Merged ${response.data.duplicate_groups_processed} duplicate groups, deleted ${response.data.accounts_deleted} accounts`);
      setDuplicateMergePassword('');
      // Refresh duplicate data
      const refreshed = await axios.get(`${API}/admin/duplicate-emails`);
      setDuplicateData(refreshed.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to merge duplicates");
    } finally {
      setIsMergingDuplicates(false);
    }
  };


  // Password Reset Handlers
  const handlePasswordResetRequest = (item) => {
    setResetPasswordItem(item);
    setShowPasswordResetAuthDialog(true);
  };

  const handlePasswordResetAuth = () => {
    if (!authPassword) {
      toast.error("Please enter authorization password");
      return;
    }
    // Store auth password temporarily to use in final submit
    setShowPasswordResetAuthDialog(false);
    setShowPasswordResetDialog(true);
  };

  const handlePasswordResetSubmit = async () => {
    if (!newPassword || newPassword.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }

    setResettingPassword(true);
    toast.info("Resetting password...", { duration: 2000 });

    try {
      const response = await axios.post(`${API}/admin/manual-password-reset`, {
        email: resetPasswordItem.parent_email,
        new_password: newPassword,
        void_password: authPassword
      });
      
      toast.success(`Password reset successful for ${resetPasswordItem.parent_name}`);
      setShowPasswordResetDialog(false);
      setResetPasswordItem(null);
      setNewPassword('');
      setAuthPassword('');
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error("Invalid authorization password");
        setShowPasswordResetDialog(false);
        setShowPasswordResetAuthDialog(true);
      } else {
        toast.error(error.response?.data?.detail || "Failed to reset password");
      }
    } finally {
      setResettingPassword(false);
    }
  };

  // Move Student Handlers
  const handleMoveStudent = (item) => {
    setMoveStudentItem({
      ...item,
      current_grade: item.students?.[0]?.grade_applying || selectedGrade
    });
    setMoveTargetGrade("");
    setShowMoveStudentDialog(true);
  };

  const handleMoveStudentSubmit = async () => {
    if (!moveTargetGrade) {
      toast.error("Please select a destination grade");
      return;
    }
    setIsMovingStudent(true);
    try {
      const response = await axios.post(`${API}/admin/move-student`, {
        application_id: moveStudentItem.application_id,
        student_index: 0,
        new_grade: moveTargetGrade
      });
      toast.success(response.data.message);
      setShowMoveStudentDialog(false);
      setMoveStudentItem(null);
      setMoveTargetGrade("");
      // Refresh the current waitlist
      if (selectedGrade) {
        fetchGradeWaitlist(selectedGrade);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to move student");
    } finally {
      setIsMovingStudent(false);
    }
  };

  // Rearrange by DOB Handler
  const handleRearrangeByDOB = async () => {
    setIsRearranging(true);
    setRearrangeResults(null);
    try {
      const response = await axios.post(`${API}/admin/rearrange-by-dob`);
      setRearrangeResults(response.data);
      if (response.data.summary.total_moved > 0) {
        toast.success(`${response.data.summary.total_moved} student(s) moved to correct grade`);
      } else {
        toast.info("All students are already in the correct grade");
      }
      // Refresh stats
      fetchStats();
      if (selectedGrade) {
        fetchGradeWaitlist(selectedGrade);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to rearrange students");
    } finally {
      setIsRearranging(false);
    }
  };

  const handleMarkProcessing = async (applicationId) => {
    try {
      await axios.post(`${API}/admin/mark-processing`, {
        application_id: applicationId
      });
      toast.success("Application marked as Processing");
      fetchGradeWaitlist(selectedGrade);
      fetchStats();
    } catch (error) {
      toast.error("Failed to mark application as Processing");
    }
  };

  const fetchProcessingApplications = async () => {
    try {
      const response = await axios.get(`${API}/admin/processing-applications`);
      setProcessingApplications(response.data);
    } catch (error) {
      toast.error("Failed to fetch processing applications");
    }
  };

  const handleShowProcessingList = () => {
    setShowProcessingList(true);
    fetchProcessingApplications();
  };


  const handleRequestReportCard = async (applicationId) => {
    try {
      await axios.post(`${API}/admin/request-report-card`, {
        application_id: applicationId
      });
      toast.success("Report card request sent successfully");
      fetchProcessingApplications();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send report card request");
    }
  };

  const handleViewDetails = (item) => {
    // Open edit dialog in view-only mode by using the existing edit dialog
    handleEditRecord(item);
  };

  const handleRejectApplication = (applicationId) => {
    setRejectingApplication(applicationId);
    setShowRejectPasswordDialog(true);
  };

  const handleRejectPasswordSubmit = () => {
    if (!rejectPassword) {
      toast.error("Please enter permission password");
      return;
    }
    setShowRejectPasswordDialog(false);
    setShowRejectReasonDialog(true);
  };

  const handleRejectReasonSubmit = async () => {
    const finalReason = rejectReason === 'Other' ? customRejectReason : rejectReason;
    
    if (!finalReason) {
      toast.error("Please provide a rejection reason");
      return;
    }

    try {
      await axios.post(`${API}/admin/reject-application`, {
        application_id: rejectingApplication,
        password: rejectPassword,
        rejection_reason: finalReason
      });
      
      toast.success("Application rejected successfully");
      setShowRejectReasonDialog(false);
      setRejectingApplication(null);
      setRejectPassword('');
      setRejectReason('');
      setCustomRejectReason('');
      
      fetchProcessingApplications();
      if (selectedGrade) fetchGradeWaitlist(selectedGrade);
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to reject application");
    }
  };

  const handleMoveBackToWaitlist = async (applicationId) => {
    if (!window.confirm("Move this application back to the waitlist?")) {
      return;
    }
    try {
      const response = await axios.post(`${API}/admin/move-to-waitlist`, {
        application_id: applicationId
      });
      console.log("Move to waitlist response:", response.data);
      toast.success("Application moved back to waitlist");
      fetchProcessingApplications();
      if (selectedGrade) fetchGradeWaitlist(selectedGrade);
    } catch (error) {
      console.error("Move to waitlist error:", error);
      console.error("Error response:", error.response);
      toast.error(error.response?.data?.detail || error.message || "Failed to move application");
    }
  };

  const handleStudentFieldChange = (index, field, value) => {
    const updatedStudents = [...editFormData.students];
    updatedStudents[index][field] = value;
    setEditFormData({...editFormData, students: updatedStudents});
  };

  const handleDeleteStudent = async (item, studentIndex) => {
    const student = item.students[studentIndex];
    const studentName = `${student.first_name} ${student.last_name}`;
    const isLastStudent = item.students.length === 1;
    
    const confirmMsg = isLastStudent
      ? `Delete "${studentName}"? This is the only student on application ${item.application_code} — the entire application will be removed.`
      : `Delete "${studentName}" from application ${item.application_code}? The application will remain with ${item.students.length - 1} other student(s).`;

    if (!window.confirm(confirmMsg)) return;

    try {
      const response = await axios.post(`${API}/admin/delete-student`, {
        application_id: item.application_id,
        student_index: studentIndex
      });
      toast.success(`${response.data.student_name} deleted`);
      if (selectedGrade) fetchGradeWaitlist(selectedGrade);
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete student");
    }
  };

  const handleLogout = () => {
    logout();
    navigate("/admin-login");
  };

  const handleExportToExcel = () => {
    if (!waitlist || waitlist.length === 0) {
      toast.error("No data to export");
      return;
    }

    const rows = waitlist.map((item) => {
      const studentNames = (item.students || []).map(s => `${s.first_name} ${s.last_name}`).join(", ");
      const dobs = (item.students || []).map(s => s.date_of_birth || "N/A").join(", ");
      return {
        "Position": item.position,
        "Application Code": item.application_code,
        "Application Date": item.applied_date || "",
        "Family Name": item.parent_name,
        "Email": item.parent_email,
        "Phone": item.parent_telephone,
        "Student(s)": studentNames,
        "Date of Birth": dobs,
        "Sibling": item.has_sibling ? "Yes" : "No",
        "Notes": item.notes || "",
        "Status": item.status,
      };
    });

    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, selectedGrade || "Waitlist");

    const today = new Date().toISOString().split("T")[0];
    XLSX.writeFile(wb, `Waitlist_${selectedGrade || "All"}_${today}.xlsx`);
    toast.success(`Exported ${rows.length} records to Excel`);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0E366A] via-[#1a4d7a] to-[#0E366A]">
      {/* Header */}
      <header className="bg-[#F7B538] border-b border-[#e5a627] sticky top-0 z-50">
        <div className="container mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              onClick={() => navigate("/admin/dashboard")}
              className="text-[#0E366A] hover:bg-[#0E366A] hover:text-white transition-colors"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Dashboard
            </Button>
            <div className="border-l border-[#0E366A]/30 pl-4">
              <h1 className="text-2xl font-bold text-[#0E366A] font-serif">Waitlist Management</h1>
              <p className="text-sm text-[#0E366A] italic">Manage application waitlists</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Button 
              variant="outline" 
              onClick={handleShowProcessingList}
              className="border-blue-600 text-blue-600 hover:bg-blue-600 hover:text-white transition-colors"
            >
              <Clock className="w-4 h-4 mr-2" />
              Under Process Applications
            </Button>
            <Button 
              variant="outline" 
              onClick={handleShowVoidedList}
              className="border-[#0E366A] text-[#0E366A] hover:bg-[#0E366A] hover:text-white transition-colors"
            >
              <XCircle className="w-4 h-4 mr-2" />
              Voided Applications
            </Button>
            <Button 
              variant="outline" 
              onClick={handleShowDuplicates}
              data-testid="merge-duplicates-btn"
              className="border-red-600 text-red-600 hover:bg-red-600 hover:text-white transition-colors"
            >
              <Users className="w-4 h-4 mr-2" />
              Merge Duplicates
            </Button>
            <Button 
              variant="outline" 
              onClick={handleLogout}
              className="border-[#0E366A] text-[#0E366A] hover:bg-[#0E366A] hover:text-white transition-colors"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-6 py-8">
        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <Card className="bg-white border-2 border-[#F7B538]">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Total Waitlist</p>
                  <p className="text-3xl font-bold text-[#0E366A]">{getTotalWaitlist()}</p>
                </div>
                <div className="w-12 h-12 bg-[#F7B538]/20 rounded-full flex items-center justify-center">
                  <Users className="w-6 h-6 text-[#0E366A]" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white border-2 border-blue-500">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Grades with Waitlist</p>
                  <p className="text-3xl font-bold text-blue-600">{getGradesWithWaitlist()}</p>
                  <p className="text-xs text-gray-500">of {stats.length} grades</p>
                </div>
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                  <Clock className="w-6 h-6 text-blue-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white border-2 border-green-500">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Available Spots</p>
                  <p className="text-3xl font-bold text-green-600">{getTotalAvailableSpots()}</p>
                </div>
                <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                  <TrendingUp className="w-6 h-6 text-green-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white border-2 border-purple-500">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Selected Grade</p>
                  <p className="text-2xl font-bold text-purple-600">{selectedGrade || "None"}</p>
                  <p className="text-xs text-gray-500">{selectedGrade ? `${waitlist.length} waiting` : "Select a grade"}</p>
                </div>
                <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center">
                  <Filter className="w-6 h-6 text-purple-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Quick Grade Navigation */}
        <Card className="bg-white mb-8">
          <CardHeader className="bg-[#0E366A]/10 border-b border-[#0E366A]">
            <CardTitle className="text-[#0E366A] flex items-center justify-between">
              <div className="flex items-center">
                <Search className="w-5 h-5 mr-2" />
                Search All Grades
              </div>
            </CardTitle>
            <CardDescription>Search across all grades at once by student name, parent name, or application code</CardDescription>
          </CardHeader>
          <CardContent className="pt-6">
            <div className="flex gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  placeholder="Search all grades by student name, parent name, or application code..."
                  value={globalSearchTerm}
                  onChange={(e) => setGlobalSearchTerm(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleGlobalSearch()}
                  className="pl-10 border-[#0E366A]/20 focus:border-[#F7B538]"
                />
              </div>
              <Button
                onClick={handleGlobalSearch}
                disabled={isGlobalSearching || !globalSearchTerm.trim()}
                className="bg-[#0E366A] hover:bg-[#1a4d7a] text-white px-8"
              >
                {isGlobalSearching ? "Searching..." : "Search All"}
              </Button>
              {showGlobalSearch && (
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowGlobalSearch(false);
                    setGlobalSearchTerm("");
                    setGlobalSearchResults([]);
                  }}
                  className="border-[#0E366A] text-[#0E366A]"
                >
                  Clear
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Quick Grade Navigation */}
        {!showGlobalSearch && (
        <Card className="bg-white mb-8">
          <CardHeader className="bg-[#F7B538]/10 border-b border-[#F7B538]">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-[#0E366A] flex items-center">
                  <Filter className="w-5 h-5 mr-2" />
                  Select Grade to View Waitlist
                </CardTitle>
                <CardDescription>Click on a grade card to view its waitlist details</CardDescription>
              </div>
              <Button
                data-testid="rearrange-by-dob-btn"
                onClick={() => setShowRearrangeDialog(true)}
                className="bg-[#0E366A] hover:bg-[#0a2a52] text-white text-sm"
              >
                <Calendar className="w-4 h-4 mr-2" />
                Rearrange by DOB
              </Button>
            </div>
          </CardHeader>
          <CardContent className="pt-6">
            <div className="grid grid-cols-2 md:grid-cols-7 gap-3">
              {stats.map(stat => {
                const isSelected = selectedGrade === stat.grade;
                const hasNoSpots = stat.available_spots === 0;
                
                let borderColor;
                if (isSelected) {
                  borderColor = '#3b82f6'; // Blue when selected
                } else if (hasNoSpots) {
                  borderColor = '#ef4444'; // Red when no spots
                } else {
                  borderColor = '#10b981'; // Green when available
                }
                
                return (
                  <Card 
                    key={stat.grade}
                    id={`grade-card-${stat.grade}`}
                    className="cursor-pointer hover:shadow-lg transition-all border-2 bg-white"
                    style={{ borderColor }}
                    onClick={() => fetchGradeWaitlist(stat.grade)}
                  >
                    <CardContent className="p-4">
                      <div className="text-center">
                        <p className="text-xl font-bold text-[#0E366A]">{stat.grade}</p>
                        <Badge 
                          variant="outline" 
                          className="mt-2"
                          style={{ 
                            borderColor, 
                            color: borderColor 
                          }}
                        >
                          {stat.total_waiting} waiting
                        </Badge>
                        <p className="text-xs text-gray-500 mt-1">{stat.available_spots} spots</p>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </CardContent>
        </Card>
        )}

        {/* Global Search Results */}
        {showGlobalSearch && (
          <Card className="bg-white mb-8">
            <CardHeader className="bg-[#0E366A]/10 border-b border-[#0E366A]">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-[#0E366A] flex items-center">
                    <Search className="w-5 h-5 mr-2" />
                    Global Search Results
                  </CardTitle>
                  <CardDescription className="mt-2">
                    {globalSearchResults.length > 0 
                      ? `Found ${globalSearchResults.length} result(s) for "${globalSearchTerm}"`
                      : `No results found for "${globalSearchTerm}"`
                    }
                  </CardDescription>
                </div>
                <Button 
                  variant="ghost" 
                  onClick={() => {
                    setShowGlobalSearch(false);
                    setGlobalSearchTerm("");
                    setGlobalSearchResults([]);
                  }}
                  className="text-[#0E366A] hover:bg-[#F7B538]/20"
                >
                  Close
                </Button>
              </div>
            </CardHeader>
            <CardContent className="pt-6">
              {globalSearchResults.length === 0 ? (
                <div className="text-center py-12">
                  <Search className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500 mb-4">No students found matching &quot;{globalSearchTerm}&quot;</p>
                  <Button 
                    onClick={() => {
                      setShowGlobalSearch(false);
                      setGlobalSearchTerm("");
                    }}
                    className="bg-[#F7B538] hover:bg-[#e5a627] text-[#0E366A]"
                  >
                    Try Another Search
                  </Button>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-[#F7B538]/10">
                        <TableHead className="text-[#0E366A] font-semibold">Grade</TableHead>
                        <TableHead className="text-[#0E366A] font-semibold">Position</TableHead>
                        <TableHead className="text-[#0E366A] font-semibold">Application Code</TableHead>
                        <TableHead className="text-[#0E366A] font-semibold">Parent Name</TableHead>
                        <TableHead className="text-[#0E366A] font-semibold">Email</TableHead>
                        <TableHead className="text-[#0E366A] font-semibold">Phone</TableHead>
                        <TableHead className="text-[#0E366A] font-semibold">Student(s)</TableHead>
                        <TableHead className="text-[#0E366A] font-semibold">Status</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {globalSearchResults.map((item, idx) => (
                        <TableRow key={item.application_id} className="hover:bg-gray-50">
                          <TableCell>
                            <Badge className="bg-[#0E366A] text-white">
                              {item.grade}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-[#0E366A] font-semibold">{item.position}</TableCell>
                          <TableCell className="text-[#0E366A] font-semibold">{item.application_code}</TableCell>
                          <TableCell className="text-[#0E366A]">{item.parent_name}</TableCell>
                          <TableCell className="text-gray-600">{item.parent_email}</TableCell>
                          <TableCell className="text-gray-600">{item.parent_telephone}</TableCell>
                          <TableCell>
                            {item.students.map((s, i) => (
                              <div key={i} className="text-sm">
                                <span className="text-[#0E366A] font-medium">
                                  {s.first_name} {s.last_name}
                                </span>
                              </div>
                            ))}
                          </TableCell>
                          <TableCell>
                            <Badge 
                              variant="outline"
                              className={
                                item.status === "Pending" ? "border-yellow-500 text-yellow-600" :
                                item.status === "Processing" ? "border-blue-500 text-blue-600" :
                                item.status === "Report Card Pending" ? "border-purple-500 text-purple-600" :
                                "border-gray-500 text-gray-600"
                              }
                            >
                              {item.status}
                            </Badge>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Waitlist View */}
        {selectedGrade && (
          <Card className="bg-white">
            <CardHeader className="bg-[#F7B538]/10 border-b border-[#F7B538]">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-[#0E366A] flex items-center">
                    <Badge className="bg-[#0E366A] text-white text-lg px-4 py-2 mr-3">
                      {selectedGrade}
                    </Badge>
                    Waitlist ({waitlist.length} students)
                  </CardTitle>
                  <CardDescription className="mt-2">
                    {searchTerm && `Showing ${filteredWaitlist.length} of ${waitlist.length} students`}
                  </CardDescription>
                </div>
                <Button 
                  data-testid="close-grade-view-btn"
                  variant="ghost" 
                  onClick={() => {
                    setSelectedGrade(null);
                    setSearchTerm("");
                  }}
                  className="text-[#0E366A] hover:bg-[#F7B538]/20"
                >
                  Close
                </Button>
              </div>
            </CardHeader>

            {/* Search Bar */}
            <div className="p-6 bg-gray-50 border-b">
              <div className="flex gap-4 items-center">
                <div className="relative flex-1 max-w-md">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    placeholder="Search by student name, parent name, or application code..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10 border-[#0E366A]/20 focus:border-[#F7B538]"
                  />
                </div>
                
                {/* Birth Year Sort Filter - For Preschool, JK, and SK */}
                {["Preschool", "JK", "SK"].includes(selectedGrade) && (
                  <div className="flex items-center gap-2">
                    <Filter className="w-4 h-4 text-[#0E366A]" />
                    <select
                      value={birthYearSort}
                      onChange={(e) => setBirthYearSort(e.target.value)}
                      className="border border-[#0E366A]/20 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-[#F7B538] bg-white"
                    >
                      <option value="none">Sort by Birth Year</option>
                      <option value="newest">Newest First</option>
                      <option value="oldest">Oldest First</option>
                    </select>
                  </div>
                )}

                <Button
                  data-testid="export-excel-btn"
                  onClick={handleExportToExcel}
                  className="bg-[#0E366A] hover:bg-[#0a2a52] text-white ml-auto"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Export to Excel
                </Button>
              </div>
            </div>

            <CardContent>
              {loading ? (
                <p className="text-center py-8 text-[#0E366A]">Loading waitlist...</p>
              ) : filteredWaitlist.length === 0 && searchTerm ? (
                <div className="text-center py-12">
                  <Search className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500 mb-4">No students found matching &ldquo;{searchTerm}&rdquo;</p>
                  <Button 
                    onClick={() => setSearchTerm("")}
                    className="bg-[#F7B538] hover:bg-[#e5a627] text-[#0E366A]"
                  >
                    Clear Search
                  </Button>
                </div>
              ) : filteredWaitlist.length === 0 ? (
                <p className="text-center text-gray-600 py-8">No applicants in the waitlist for this grade.</p>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-[#F7B538]/10">
                        <TableHead className="text-[#0E366A] font-semibold">Position</TableHead>
                        <TableHead className="text-[#0E366A] font-semibold">Application Code</TableHead>
                        <TableHead className="text-[#0E366A] font-semibold">Application Date</TableHead>
                        <TableHead className="text-[#0E366A] font-semibold">Family Name</TableHead>
                        <TableHead className="text-[#0E366A] font-semibold">Email</TableHead>
                        <TableHead className="text-[#0E366A] font-semibold">Phone</TableHead>
                        <TableHead className="text-[#0E366A] font-semibold">Student(s)</TableHead>
                        <TableHead className="text-[#0E366A] font-semibold">Date of Birth</TableHead>
                        <TableHead className="text-[#0E366A] font-semibold">Sibling</TableHead>
                        <TableHead className="text-[#0E366A] font-semibold">Notes</TableHead>
                        <TableHead className="text-[#0E366A] font-semibold">Status</TableHead>
                        <TableHead className="text-[#0E366A] font-semibold">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredWaitlist.map((item, idx) => (
                        <TableRow key={item.application_id} className={idx === 0 ? 'bg-[#F7B538]/10' : ''}>
                          <TableCell className="font-semibold text-[#0E366A]">#{item.position}</TableCell>
                          <TableCell className="text-[#0E366A]">{item.application_code}</TableCell>
                          <TableCell className="text-sm text-gray-600">{item.applied_date}</TableCell>
                          <TableCell className="text-[#0E366A]">{item.parent_name}</TableCell>
                          <TableCell className="text-gray-600">{item.parent_email}</TableCell>
                          <TableCell className="text-gray-600">{item.parent_telephone}</TableCell>
                          <TableCell>
                            {item.students.map((s, i) => (
                              <div key={i} className="text-[#0E366A]">{s.first_name} {s.last_name}</div>
                            ))}
                          </TableCell>
                          <TableCell>
                              {item.students.map((s, i) => (
                                <div key={i} className="text-gray-600 text-sm">
                                  {s.date_of_birth ? new Date(s.date_of_birth).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }) : 'N/A'}
                                </div>
                              ))}
                            </TableCell>
                          <TableCell>
                            {item.has_sibling ? (
                              <span className="text-[#F7B538] font-medium">Yes</span>
                            ) : (
                              <span className="text-gray-400">No</span>
                            )}
                          </TableCell>
                          <TableCell>
                            <span className="text-gray-600 text-sm max-w-xs truncate block" title={item.notes || ''}>
                              {item.notes || '-'}
                            </span>
                          </TableCell>
                          <TableCell>
                            <div className="flex flex-col gap-1">
                              <span className={`px-2 py-1 rounded text-xs font-medium ${
                                item.status === 'Pending' ? 'bg-yellow-100 text-yellow-800' : 
                                item.status === 'Processing' ? 'bg-purple-100 text-purple-800' : 
                                'bg-blue-100 text-blue-800'
                              }`}>
                                {item.status}
                              </span>
                              {item.keep_in_jk_waitlist_next_year === true && selectedGrade === "Preschool" && (
                                <span className="px-2 py-1 rounded text-xs font-medium bg-green-100 text-green-800 whitespace-nowrap">
                                  Keep in JK WL Next Year
                                </span>
                              )}
                            </div>
                          </TableCell>
                          <TableCell>
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button 
                                  variant="ghost" 
                                  size="sm"
                                  data-testid={`actions-menu-${idx}`}
                                  className="h-8 w-8 p-0 text-[#0E366A] hover:bg-[#F7B538]/20"
                                >
                                  <MoreVertical className="h-4 w-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem
                                  data-testid={`edit-btn-${idx}`}
                                  onClick={() => handleEditRecord(item)}
                                  className="cursor-pointer"
                                >
                                  <Edit className="w-4 h-4 mr-2" />
                                  Edit Record
                                </DropdownMenuItem>
                                <DropdownMenuItem
                                  data-testid={`interview-btn-${idx}`}
                                  onClick={() => handleRequestInterview(item.application_id, selectedGrade)}
                                  disabled={item.status === 'Interview Requested'}
                                  className="cursor-pointer"
                                >
                                  <Mail className="w-4 h-4 mr-2" />
                                  Request Interview
                                </DropdownMenuItem>
                                <DropdownMenuItem
                                  data-testid={`processing-btn-${idx}`}
                                  onClick={() => handleMarkProcessing(item.application_id)}
                                  disabled={item.status === 'Processing'}
                                  className="cursor-pointer text-blue-600 focus:text-blue-600"
                                >
                                  <Clock className="w-4 h-4 mr-2" />
                                  Mark as Processing
                                </DropdownMenuItem>
                                <DropdownMenuItem
                                  data-testid={`accept-btn-${idx}`}
                                  onClick={() => handleAcceptStudent(item.application_id, selectedGrade)}
                                  className="cursor-pointer text-green-600 focus:text-green-600"
                                >
                                  <CheckCircle className="w-4 h-4 mr-2" />
                                  Accept Student
                                </DropdownMenuItem>
                                <DropdownMenuItem
                                  data-testid={`void-btn-${idx}`}
                                  onClick={() => handleVoidApplication(item)}
                                  className="cursor-pointer text-red-600 focus:text-red-600"
                                >
                                  <XCircle className="w-4 h-4 mr-2" />
                                  Void Application
                                </DropdownMenuItem>
                                <DropdownMenuItem
                                  data-testid={`reset-password-btn-${idx}`}
                                  onClick={() => handlePasswordResetRequest(item)}
                                  className="cursor-pointer text-purple-600 focus:text-purple-600"
                                >
                                  <Key className="w-4 h-4 mr-2" />
                                  Reset Password
                                </DropdownMenuItem>
                                <DropdownMenuItem
                                  data-testid={`move-student-btn-${idx}`}
                                  onClick={() => handleMoveStudent(item)}
                                  className="cursor-pointer text-blue-600 focus:text-blue-600"
                                >
                                  <ArrowRightLeft className="w-4 h-4 mr-2" />
                                  Move Student
                                </DropdownMenuItem>
                                {item.students.length === 1 ? (
                                  <DropdownMenuItem
                                    data-testid={`delete-student-btn-${idx}`}
                                    onClick={() => handleDeleteStudent(item, 0)}
                                    className="cursor-pointer text-red-600 focus:text-red-600"
                                  >
                                    <Trash2 className="w-4 h-4 mr-2" />
                                    Delete Student
                                  </DropdownMenuItem>
                                ) : (
                                  item.students.map((s, si) => (
                                    <DropdownMenuItem
                                      key={si}
                                      data-testid={`delete-student-btn-${idx}-${si}`}
                                      onClick={() => handleDeleteStudent(item, si)}
                                      className="cursor-pointer text-red-600 focus:text-red-600"
                                    >
                                      <Trash2 className="w-4 h-4 mr-2" />
                                      Delete {s.first_name} {s.last_name}
                                    </DropdownMenuItem>
                                  ))
                                )}
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </div>

      {/* Edit Record Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-[#0E366A]">Edit Application Record</DialogTitle>
            <DialogDescription>
              Edit student and parent information. Position cannot be changed.
            </DialogDescription>
          </DialogHeader>
          
          {editingApplication && (
            <div className="space-y-6">
              {/* Parent Information */}
              <div className="space-y-4">
                <h3 className="font-semibold text-[#0E366A]">Parent Information</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Parent Name</Label>
                    <Input
                      value={editFormData.parent_name}
                      onChange={(e) => setEditFormData({...editFormData, parent_name: e.target.value})}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Email</Label>
                    <Input
                      type="email"
                      value={editFormData.parent_email}
                      onChange={(e) => setEditFormData({...editFormData, parent_email: e.target.value})}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Telephone</Label>
                    <Input
                      value={editFormData.parent_telephone}
                      onChange={(e) => setEditFormData({...editFormData, parent_telephone: e.target.value})}
                    />
                  </div>
                </div>
                <div className="space-y-2 col-span-2">
                  <Label>Notes</Label>
                  <textarea
                    className="w-full min-h-[100px] px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#F7B538] focus:border-transparent"
                    placeholder="Add notes about this application..."
                    value={editFormData.notes}
                    onChange={(e) => setEditFormData({...editFormData, notes: e.target.value})}
                  />
                </div>
              </div>

              {/* Student Information */}
              <div className="space-y-4">
                <h3 className="font-semibold text-[#0E366A]">Student Information</h3>
                {editFormData.students.map((student, idx) => (
                  <Card key={idx} className="p-4 bg-[#F7B538]/5">
                    <h4 className="font-medium text-[#0E366A] mb-3">Student {idx + 1}</h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>First Name</Label>
                        <Input
                          value={student.first_name}
                          onChange={(e) => handleStudentFieldChange(idx, 'first_name', e.target.value)}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Last Name</Label>
                        <Input
                          value={student.last_name}
                          onChange={(e) => handleStudentFieldChange(idx, 'last_name', e.target.value)}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Gender</Label>
                        <Input
                          value={student.gender}
                          onChange={(e) => handleStudentFieldChange(idx, 'gender', e.target.value)}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Date of Birth</Label>
                        <Input
                          type="date"
                          value={student.date_of_birth}
                          onChange={(e) => handleStudentFieldChange(idx, 'date_of_birth', e.target.value)}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Grade Applying</Label>
                        <Input
                          value={student.grade_applying}
                          disabled
                          className="bg-gray-100"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Current School</Label>
                        <Input
                          value={student.current_school}
                          onChange={(e) => handleStudentFieldChange(idx, 'current_school', e.target.value)}
                        />
                      </div>
                    </div>
                  </Card>
                ))}
              </div>

              {/* Position Display (Read-only) */}
              <div className="bg-gray-50 p-4 rounded-lg border border-gray-300">
                <p className="text-sm text-gray-700">
                  <span className="font-semibold text-[#0E366A]">Current Position:</span> #{editingApplication.position}
                  <span className="ml-4 text-gray-500">(Position cannot be edited)</span>
                </p>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleSaveEdit}
              className="bg-[#F7B538] hover:bg-[#e5a627] text-[#0E366A] font-semibold"
            >
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


      {/* Void Password Dialog */}
      <Dialog open={showVoidPasswordDialog} onOpenChange={setShowVoidPasswordDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-[#0E366A]">Void Application - Authorization Required</DialogTitle>
            <DialogDescription>
              This action requires special permission. Please enter the void permission password.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Permission Password</Label>
              <Input
                type="password"
                value={voidPassword}
                onChange={(e) => setVoidPassword(e.target.value)}
                placeholder="Enter void permission password"
                onKeyPress={(e) => e.key === 'Enter' && handleVoidPasswordSubmit()}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowVoidPasswordDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleVoidPasswordSubmit}
              className="bg-[#F7B538] hover:bg-[#e5a627] text-[#0E366A] font-semibold"
            >
              Continue
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Void Reason Dialog */}
      <Dialog open={showVoidReasonDialog} onOpenChange={setShowVoidReasonDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-[#0E366A]">Void Application - Select Reason</DialogTitle>
            <DialogDescription>
              Please select a reason for voiding this application.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Void Reason</Label>
              <Select value={voidReason} onValueChange={setVoidReason}>
                <SelectTrigger>
                  <SelectValue placeholder="Select a reason" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Parent not interested anymore">Parent not interested anymore</SelectItem>
                  <SelectItem value="Failed interview">Failed interview</SelectItem>
                  <SelectItem value="No response phone/email">No response phone/email</SelectItem>
                  <SelectItem value="other">Other (please specify)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {voidReason === 'other' && (
              <div className="space-y-2">
                <Label>Specify Reason</Label>
                <Input
                  value={customVoidReason}
                  onChange={(e) => setCustomVoidReason(e.target.value)}
                  placeholder="Enter the reason"
                />
              </div>
            )}
          </div>
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setShowVoidReasonDialog(false)}
              disabled={isVoiding}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleVoidReasonSubmit}
              disabled={isVoiding}
              className="bg-red-600 hover:bg-red-700 text-white disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isVoiding ? "Voiding..." : "Void Application"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reject Application - Password Dialog */}
      <Dialog open={showRejectPasswordDialog} onOpenChange={setShowRejectPasswordDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-[#0E366A]">Reject Application - Authorization Required</DialogTitle>
            <DialogDescription>
              This action requires special permission. Please enter the reject permission password.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Permission Password</Label>
              <Input
                type="password"
                value={rejectPassword}
                onChange={(e) => setRejectPassword(e.target.value)}
                placeholder="Enter reject permission password"
                onKeyPress={(e) => e.key === 'Enter' && handleRejectPasswordSubmit()}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRejectPasswordDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleRejectPasswordSubmit}
              className="bg-[#F7B538] hover:bg-[#e5a627] text-[#0E366A] font-semibold"
            >
              Continue
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reject Application - Reason Dialog */}
      <Dialog open={showRejectReasonDialog} onOpenChange={setShowRejectReasonDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-[#0E366A]">Reject Application - Select Reason</DialogTitle>
            <DialogDescription>
              Please provide a reason for rejecting this application.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Rejection Reason</Label>
              <Select value={rejectReason} onValueChange={setRejectReason}>
                <SelectTrigger>
                  <SelectValue placeholder="Select a reason" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Does not meet admission criteria">Does not meet admission criteria</SelectItem>
                  <SelectItem value="Failed interview">Failed interview</SelectItem>
                  <SelectItem value="Incomplete documentation">Incomplete documentation</SelectItem>
                  <SelectItem value="Other">Other (please specify)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {rejectReason === 'Other' && (
              <div className="space-y-2">
                <Label>Specify Reason</Label>
                <Input
                  value={customRejectReason}
                  onChange={(e) => setCustomRejectReason(e.target.value)}
                  placeholder="Enter the reason"
                />
              </div>
            )}
          </div>
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setShowRejectReasonDialog(false)}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleRejectReasonSubmit}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              Reject Application
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Password Reset - Authorization Dialog */}
      <Dialog open={showPasswordResetAuthDialog} onOpenChange={setShowPasswordResetAuthDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-[#0E366A]">Reset Parent Password - Authorization Required</DialogTitle>
            <DialogDescription>
              Enter the void permission password to authorize password reset for this parent account.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Authorization Password</Label>
              <Input
                type="password"
                value={authPassword}
                onChange={(e) => setAuthPassword(e.target.value)}
                placeholder="Enter void permission password"
                onKeyPress={(e) => e.key === 'Enter' && handlePasswordResetAuth()}
              />
            </div>
            {resetPasswordItem && (
              <div className="bg-blue-50 p-3 rounded">
                <p className="text-sm text-blue-900">
                  <strong>Parent:</strong> {resetPasswordItem.parent_name}
                </p>
                <p className="text-sm text-blue-900">
                  <strong>Email:</strong> {resetPasswordItem.parent_email}
                </p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => {
                setShowPasswordResetAuthDialog(false);
                setResetPasswordItem(null);
                setAuthPassword('');
              }}
            >
              Cancel
            </Button>
            <Button 
              onClick={handlePasswordResetAuth}
              className="bg-[#F7B538] hover:bg-[#e5a627] text-[#0E366A] font-semibold"
            >
              Continue
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Password Reset - New Password Dialog */}
      <Dialog open={showPasswordResetDialog} onOpenChange={setShowPasswordResetDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-[#0E366A]">Set New Password</DialogTitle>
            <DialogDescription>
              Enter a new password for this parent account. Minimum 6 characters.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {resetPasswordItem && (
              <div className="bg-blue-50 p-3 rounded">
                <p className="text-sm text-blue-900">
                  <strong>Parent:</strong> {resetPasswordItem.parent_name}
                </p>
                <p className="text-sm text-blue-900">
                  <strong>Email:</strong> {resetPasswordItem.parent_email}
                </p>
              </div>
            )}
            <div className="space-y-2">
              <Label>New Password</Label>
              <Input
                type="text"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Enter new password (min 6 characters)"
                onKeyPress={(e) => e.key === 'Enter' && handlePasswordResetSubmit()}
              />
              <p className="text-xs text-gray-500">
                The parent will use this password to login. Make sure to communicate it securely.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => {
                setShowPasswordResetDialog(false);
                setResetPasswordItem(null);
                setNewPassword('');
              }}
            >
              Cancel
            </Button>
            <Button 
              onClick={handlePasswordResetSubmit}
              disabled={resettingPassword}
              className="bg-purple-600 hover:bg-purple-700 text-white"
            >
              {resettingPassword ? "Resetting..." : "Reset Password"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Voided Applications List Dialog */}
      <Dialog open={showVoidedList} onOpenChange={(open) => { setShowVoidedList(open); if (!open) setVoidedSearchTerm(""); }}>
        <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-[#0E366A]">Voided Applications</DialogTitle>
            <DialogDescription>
              View all voided applications across all grades
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <Input
                data-testid="voided-search-input"
                placeholder="Search by code, parent name, email, or student name..."
                value={voidedSearchTerm}
                onChange={(e) => setVoidedSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            {(() => {
              const filtered = voidedApplications.filter(item => {
                if (!voidedSearchTerm) return true;
                const term = voidedSearchTerm.toLowerCase();
                return (
                  (item.application_code || "").toLowerCase().includes(term) ||
                  (item.parent_name || "").toLowerCase().includes(term) ||
                  (item.parent_email || "").toLowerCase().includes(term) ||
                  (item.void_reason || "").toLowerCase().includes(term) ||
                  (item.students || []).some(s => 
                    `${s.first_name} ${s.last_name}`.toLowerCase().includes(term)
                  )
                );
              });
              return filtered.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  {voidedSearchTerm ? "No matching voided applications found" : "No voided applications found"}
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <p className="text-xs text-gray-500 mb-2">{filtered.length} result(s)</p>
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-[#F7B538]/10">
                        <TableHead className="text-[#0E366A] font-semibold">Application Code</TableHead>
                        <TableHead className="text-[#0E366A] font-semibold">Parent Name</TableHead>
                        <TableHead className="text-[#0E366A] font-semibold">Email</TableHead>
                        <TableHead className="text-[#0E366A] font-semibold">Phone</TableHead>
                        <TableHead className="text-[#0E366A] font-semibold">Grades</TableHead>
                        <TableHead className="text-[#0E366A] font-semibold">Student(s)</TableHead>
                        <TableHead className="text-[#0E366A] font-semibold">Void Reason</TableHead>
                        <TableHead className="text-[#0E366A] font-semibold">Voided Date</TableHead>
                        <TableHead className="text-[#0E366A] font-semibold">Action</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filtered.map((item, idx) => (
                        <TableRow key={item.application_id}>
                          <TableCell className="text-[#0E366A] font-semibold">{item.application_code}</TableCell>
                          <TableCell className="text-[#0E366A]">{item.parent_name}</TableCell>
                          <TableCell className="text-gray-600">{item.parent_email}</TableCell>
                          <TableCell className="text-gray-600">{item.parent_telephone}</TableCell>
                          <TableCell className="text-[#0E366A]">{item.grades}</TableCell>
                          <TableCell>
                            {item.students.map((s, i) => (
                              <div key={i} className="text-[#0E366A]">{s.first_name} {s.last_name}</div>
                            ))}
                          </TableCell>
                          <TableCell className="text-gray-700">{item.void_reason}</TableCell>
                          <TableCell className="text-sm text-gray-600">{item.voided_date}</TableCell>
                          <TableCell>
                            <Button
                              size="sm"
                              data-testid={`restore-voided-btn-${idx}`}
                              onClick={() => handleRestoreVoidedApplication(item.application_id, item.application_code)}
                              className="bg-green-600 hover:bg-green-700 text-white text-xs"
                            >
                              <Undo2 className="w-3 h-3 mr-1" />
                              Restore
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              );
            })()}
          </div>
          <DialogFooter>
            <Button onClick={() => setShowVoidedList(false)} className="bg-[#F7B538] hover:bg-[#e5a627] text-[#0E366A]">
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Merge Duplicate Emails Dialog */}
      <Dialog open={showDuplicateDialog} onOpenChange={(open) => { setShowDuplicateDialog(open); if (!open) { setMergeResult(null); setDuplicateMergePassword(''); } }}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-[#0E366A]">Merge Duplicate Email Accounts</DialogTitle>
            <DialogDescription>
              Identify and merge parent accounts with duplicate emails (case-insensitive)
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {!duplicateData ? (
              <p className="text-center py-8 text-gray-500">Loading duplicate data...</p>
            ) : duplicateData.total_duplicate_groups === 0 ? (
              <div className="text-center py-8">
                <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-3" />
                <p className="text-green-700 font-semibold">No duplicate emails found!</p>
                <p className="text-gray-500 text-sm">All parent accounts have unique emails.</p>
              </div>
            ) : (
              <>
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <p className="text-red-800 font-semibold">{duplicateData.total_duplicate_groups} duplicate email groups found</p>
                  <p className="text-red-600 text-sm">{duplicateData.total_duplicate_accounts} total accounts ({duplicateData.total_duplicate_accounts - duplicateData.total_duplicate_groups} to be removed)</p>
                  <p className="text-gray-600 text-xs mt-1">For each group, the account with the most applications is kept. Applications from other accounts are reassigned to the kept account.</p>
                </div>
                
                {mergeResult && !mergeResult.dry_run && (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                    <p className="text-green-800 font-semibold">Merge completed successfully!</p>
                    <p className="text-green-600 text-sm">
                      {mergeResult.duplicate_groups_processed} groups merged, {mergeResult.accounts_deleted} accounts deleted, {mergeResult.applications_reassigned} applications reassigned
                    </p>
                  </div>
                )}

                <div className="max-h-[200px] overflow-y-auto border rounded-lg">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-red-50">
                        <TableHead className="text-[#0E366A] font-semibold">Email</TableHead>
                        <TableHead className="text-[#0E366A] font-semibold">Accounts</TableHead>
                        <TableHead className="text-[#0E366A] font-semibold">Keep</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {duplicateData.groups.slice(0, 50).map((g) => (
                        <TableRow key={g.email}>
                          <TableCell className="text-sm">{g.email}</TableCell>
                          <TableCell>
                            <Badge variant="destructive">{g.account_count}</Badge>
                          </TableCell>
                          <TableCell className="text-xs text-gray-600">{g.accounts[0]?.name} ({g.accounts[0]?.application_count} apps)</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                  {duplicateData.groups.length > 50 && (
                    <p className="text-xs text-gray-400 text-center py-2">Showing first 50 of {duplicateData.groups.length} groups</p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label className="text-[#0E366A]">Authorization Password</Label>
                  <Input
                    type="password"
                    data-testid="merge-password-input"
                    placeholder="Enter admin authorization password"
                    value={duplicateMergePassword}
                    onChange={(e) => setDuplicateMergePassword(e.target.value)}
                  />
                </div>
              </>
            )}
          </div>
          <DialogFooter className="gap-2">
            <Button onClick={() => setShowDuplicateDialog(false)} variant="outline">Cancel</Button>
            {duplicateData && duplicateData.total_duplicate_groups > 0 && (
              <Button
                data-testid="merge-execute-btn"
                onClick={handleMergeDuplicates}
                disabled={isMergingDuplicates || !duplicateMergePassword}
                className="bg-red-600 hover:bg-red-700 text-white"
              >
                {isMergingDuplicates ? "Merging..." : `Merge ${duplicateData.total_duplicate_groups} Groups`}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Interview Date Dialog */}
      <Dialog open={showInterviewDateDialog} onOpenChange={setShowInterviewDateDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-[#0E366A]">Schedule Interview</DialogTitle>
            <DialogDescription>
              Select date and time for the interview
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-[#0E366A] mb-2">Interview Date</label>
              <Input
                type="date"
                value={interviewDate}
                onChange={(e) => setInterviewDate(e.target.value)}
                className="border-[#0E366A]/20 focus:border-[#F7B538]"
                min={new Date().toISOString().split('T')[0]}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#0E366A] mb-2">Interview Time (before 3:00 PM)</label>
              <Input
                type="time"
                value={interviewTime}
                onChange={(e) => setInterviewTime(e.target.value)}
                max="15:00"
                className="border-[#0E366A]/20 focus:border-[#F7B538]"
              />
              <p className="text-xs text-gray-500 mt-1">Interviews must be scheduled before 3:00 PM</p>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowInterviewDateDialog(false);
                setInterviewDate('');
                setInterviewTime('');
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleInterviewDateSubmit}
              className="bg-[#0E366A] hover:bg-[#0E366A]/90 text-white"
            >
              Schedule Interview
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Under Process Applications List Dialog */}
      <Dialog open={showProcessingList} onOpenChange={setShowProcessingList}>
        <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-[#0E366A]">Under Process Applications</DialogTitle>
            <DialogDescription>
              View all applications currently under process
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {processingApplications.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                No applications in processing status
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-blue-50">
                      <TableHead className="text-[#0E366A] font-semibold">Application Code</TableHead>
                      <TableHead className="text-[#0E366A] font-semibold">Parent Name</TableHead>
                      <TableHead className="text-[#0E366A] font-semibold">Email</TableHead>
                      <TableHead className="text-[#0E366A] font-semibold">Phone</TableHead>
                      <TableHead className="text-[#0E366A] font-semibold">Grades</TableHead>
                      <TableHead className="text-[#0E366A] font-semibold">Student(s)</TableHead>
                      <TableHead className="text-[#0E366A] font-semibold">Status</TableHead>
                      <TableHead className="text-[#0E366A] font-semibold">Notes</TableHead>
                      <TableHead className="text-[#0E366A] font-semibold">Last Updated</TableHead>
                      <TableHead className="text-[#0E366A] font-semibold">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {processingApplications.map((item, idx) => (
                      <TableRow key={item.application_id}>
                        <TableCell className="text-[#0E366A] font-semibold">{item.application_code}</TableCell>
                        <TableCell className="text-[#0E366A]">{item.parent_name}</TableCell>
                        <TableCell className="text-gray-600">{item.parent_email}</TableCell>
                        <TableCell className="text-gray-600">{item.parent_telephone}</TableCell>
                        <TableCell className="text-[#0E366A]">{item.grades}</TableCell>
                        <TableCell>
                          {item.students.map((s, i) => (
                            <div key={i} className="text-[#0E366A]">{s.first_name} {s.last_name}</div>
                          ))}
                        </TableCell>
                        <TableCell>
                          <span className={`px-2 py-1 rounded text-xs font-medium ${
                            item.status === 'Report Card Pending' 
                              ? 'bg-orange-100 text-orange-800' 
                              : 'bg-purple-100 text-purple-800'
                          }`}>
                            {item.status}
                          </span>
                          {item.report_card_requested_at && (
                            <div className="text-xs text-gray-500 mt-1">
                              Requested: {new Date(item.report_card_requested_at).toLocaleDateString()}
                            </div>
                          )}
                        </TableCell>
                        <TableCell className="text-gray-700 max-w-xs truncate">{item.notes || '-'}</TableCell>
                        <TableCell className="text-sm text-gray-600">{item.updated_date}</TableCell>
                        <TableCell>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                                <MoreVertical className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-56">
                              <DropdownMenuItem onClick={() => handleViewDetails(item)}>
                                <Eye className="mr-2 h-4 w-4" />
                                View Details
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => handleEditRecord(item)}>
                                <Edit className="mr-2 h-4 w-4" />
                                Edit Application
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => handleRequestInterview(item.application_id, item.grades)}>
                                <Calendar className="mr-2 h-4 w-4" />
                                Schedule Interview
                              </DropdownMenuItem>
                              {item.status === 'Processing' && (
                                <DropdownMenuItem onClick={() => handleRequestReportCard(item.application_id)}>
                                  <FileText className="mr-2 h-4 w-4" />
                                  Request Report Card
                                </DropdownMenuItem>
                              )}
                              <DropdownMenuItem onClick={() => handleAcceptStudent(item.application_id, item.grades)}>
                                <CheckCircle className="mr-2 h-4 w-4 text-green-600" />
                                Accept Application
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => handleRejectApplication(item.application_id)} className="text-red-600">
                                <XCircle className="mr-2 h-4 w-4" />
                                Reject Application
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => handleMoveBackToWaitlist(item.application_id)}>
                                <ArrowLeft className="mr-2 h-4 w-4" />
                                Move to Waitlist
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button onClick={() => setShowProcessingList(false)} className="bg-[#F7B538] hover:bg-[#e5a627] text-[#0E366A]">
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Rearrange by DOB Dialog */}
      <Dialog open={showRearrangeDialog} onOpenChange={(open) => { setShowRearrangeDialog(open); if (!open) setRearrangeResults(null); }}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogTitle className="text-[#0E366A] font-semibold text-lg">Rearrange Students by Date of Birth</DialogTitle>
          
          {!rearrangeResults ? (
            <div className="space-y-4 mt-2">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm space-y-2">
                <p className="font-semibold text-[#0E366A]">This will check all waitlisted students and move them to the correct grade based on birth year:</p>
                <div className="grid grid-cols-2 gap-1 text-xs text-gray-600 mt-2">
                  <span>JK = Born 2022</span><span>SK = Born 2021</span>
                  <span>G1 = Born 2020</span><span>G2 = Born 2019</span>
                  <span>G3 = Born 2018</span><span>G4 = Born 2017</span>
                  <span>G5 = Born 2016</span><span>G6 = Born 2015</span>
                  <span>G7 = Born 2014</span><span>G8 = Born 2013</span>
                </div>
                <p className="text-xs text-gray-500 mt-2">Preschool students will be skipped. Position is maintained by original date of entry.</p>
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setShowRearrangeDialog(false)}>Cancel</Button>
                <Button
                  data-testid="rearrange-confirm-btn"
                  onClick={handleRearrangeByDOB}
                  disabled={isRearranging}
                  className="bg-[#0E366A] hover:bg-[#0a2a52] text-white"
                >
                  {isRearranging ? "Processing..." : "Run Rearrange"}
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-4 mt-2">
              {/* Summary */}
              <div className="grid grid-cols-3 gap-3">
                <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold text-green-700">{rearrangeResults.summary.total_moved}</p>
                  <p className="text-xs text-green-600">Moved</p>
                </div>
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold text-yellow-700">{rearrangeResults.summary.total_flagged}</p>
                  <p className="text-xs text-yellow-600">Flagged</p>
                </div>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold text-blue-700">{rearrangeResults.summary.already_correct}</p>
                  <p className="text-xs text-blue-600">Already Correct</p>
                </div>
              </div>

              {/* Moved Students */}
              {rearrangeResults.moved.length > 0 && (
                <div>
                  <h4 className="font-semibold text-green-700 text-sm mb-2">Moved Students</h4>
                  <div className="max-h-40 overflow-y-auto border rounded-lg">
                    <table className="w-full text-xs">
                      <thead className="bg-green-50 sticky top-0">
                        <tr>
                          <th className="px-2 py-1 text-left">Code</th>
                          <th className="px-2 py-1 text-left">Student</th>
                          <th className="px-2 py-1 text-left">Born</th>
                          <th className="px-2 py-1 text-left">From</th>
                          <th className="px-2 py-1 text-left">To</th>
                        </tr>
                      </thead>
                      <tbody>
                        {rearrangeResults.moved.map((m, i) => (
                          <tr key={i} className="border-t">
                            <td className="px-2 py-1">{m.application_code}</td>
                            <td className="px-2 py-1">{m.student_name}</td>
                            <td className="px-2 py-1">{m.birth_year}</td>
                            <td className="px-2 py-1 text-red-600">{m.old_grade}</td>
                            <td className="px-2 py-1 text-green-600 font-semibold">{m.new_grade}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Flagged Students */}
              {rearrangeResults.flagged.length > 0 && (
                <div>
                  <h4 className="font-semibold text-yellow-700 text-sm mb-2">Flagged for Manual Review</h4>
                  <div className="max-h-40 overflow-y-auto border rounded-lg">
                    <table className="w-full text-xs">
                      <thead className="bg-yellow-50 sticky top-0">
                        <tr>
                          <th className="px-2 py-1 text-left">Code</th>
                          <th className="px-2 py-1 text-left">Student</th>
                          <th className="px-2 py-1 text-left">Grade</th>
                          <th className="px-2 py-1 text-left">Reason</th>
                        </tr>
                      </thead>
                      <tbody>
                        {rearrangeResults.flagged.map((f, i) => (
                          <tr key={i} className="border-t">
                            <td className="px-2 py-1">{f.application_code}</td>
                            <td className="px-2 py-1">{f.student_name}</td>
                            <td className="px-2 py-1">{f.current_grade}</td>
                            <td className="px-2 py-1 text-yellow-600">{f.reason}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              <div className="flex justify-end">
                <Button onClick={() => { setShowRearrangeDialog(false); setRearrangeResults(null); }} className="bg-[#0E366A] hover:bg-[#0a2a52] text-white">
                  Close
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Move Student Dialog */}
      <Dialog open={showMoveStudentDialog} onOpenChange={setShowMoveStudentDialog}>

        <DialogContent className="max-w-md">
          <DialogTitle className="text-[#0E366A] font-semibold text-lg">Move Student to Different Grade</DialogTitle>
          {moveStudentItem && (
            <div className="space-y-4 mt-2">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm space-y-1">
                <p><span className="font-semibold text-[#0E366A]">Student:</span> {moveStudentItem.student_name}</p>
                <p><span className="font-semibold text-[#0E366A]">Application:</span> {moveStudentItem.application_code}</p>
                <p><span className="font-semibold text-[#0E366A]">Current Grade:</span> {moveStudentItem.current_grade || selectedGrade}</p>
              </div>
              <div>
                <Label className="text-sm font-medium text-[#0E366A]">Destination Grade</Label>
                <select
                  data-testid="move-student-grade-select"
                  value={moveTargetGrade}
                  onChange={(e) => setMoveTargetGrade(e.target.value)}
                  className="w-full mt-1 border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0E366A]"
                >
                  <option value="">Select grade...</option>
                  {CONSOLIDATED_GRADES.filter(g => g !== (moveStudentItem.current_grade || selectedGrade)).map(grade => (
                    <option key={grade} value={grade}>{grade}</option>
                  ))}
                </select>
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button
                  variant="outline"
                  onClick={() => { setShowMoveStudentDialog(false); setMoveStudentItem(null); }}
                  className="text-sm"
                >
                  Cancel
                </Button>
                <Button
                  data-testid="move-student-submit-btn"
                  onClick={handleMoveStudentSubmit}
                  disabled={!moveTargetGrade || isMovingStudent}
                  className="bg-[#0E366A] hover:bg-[#0a2a52] text-white text-sm"
                >
                  {isMovingStudent ? "Moving..." : "Move Student"}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default WaitlistManagement;